import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, Col, Divider, Input, Row, Typography } from 'antd';
import {
    TbSearch,
    TbShieldCheck,
    TbShieldPlus,
    TbUserCircle,
    TbUserHexagon,
    TbUserPentagon,
    TbUserSearch,
    TbUserSquareRounded,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection, VirtualizedList as List, PlanTag, SkeletonList } from '@/shared';
import { useRoleStore } from '@/stores/database/entities/00-RBAC/04-Roles';
import { MemberPermissionType, Role } from '@/types';

import CreateRoleModal from './components/CreateRoleModal';
import RoleListItem from './RoleListItem';

export interface RoleListEntry {
    id: string;
    key: string;
    label: string;
    icon: ReactNode;
    system: boolean;
    type?: MemberPermissionType;
    role?: Role;
}

const Roles: FC = (): JSX.Element => {
    const [value, setValue] = useState('');
    const [open, setOpen] = useState(false);

    const { token } = useAntDesignTheme();
    const { fetchRoles, loading, roles } = useRoleStore();

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const builtInRoles: RoleListEntry[] = useMemo(
        () => [
            {
                id: MemberPermissionType.OWNER,
                key: MemberPermissionType.OWNER,
                label: 'Owner',
                icon: (
                    <TbUserCircle
                        size='75%'
                        style={{
                            verticalAlign: '-0.125rem',
                            color: token.colorTextSecondary,
                        }}
                    />
                ),
                system: true,
                type: MemberPermissionType.OWNER,
            },
            {
                id: MemberPermissionType.ADMIN,
                key: MemberPermissionType.ADMIN,
                label: 'Admin',
                icon: (
                    <TbUserPentagon
                        size='75%'
                        style={{
                            verticalAlign: '-0.125rem',
                            color: token.colorTextSecondary,
                        }}
                    />
                ),
                system: true,
                type: MemberPermissionType.ADMIN,
            },
            {
                id: MemberPermissionType.MEMBER,
                key: MemberPermissionType.MEMBER,
                label: 'Member',
                icon: (
                    <TbUserHexagon
                        size='75%'
                        style={{
                            verticalAlign: '-0.125rem',
                            color: token.colorTextSecondary,
                        }}
                    />
                ),
                system: true,
                type: MemberPermissionType.MEMBER,
            },
            {
                id: MemberPermissionType.GUEST,
                key: MemberPermissionType.GUEST,
                label: 'Guest',
                icon: (
                    <TbUserSquareRounded
                        size='75%'
                        style={{
                            verticalAlign: '-0.125rem',
                            color: token.colorTextSecondary,
                        }}
                    />
                ),
                system: true,
                type: MemberPermissionType.GUEST,
            },
        ],
        [token.colorTextSecondary],
    );

    const customRoles = useMemo<RoleListEntry[]>(
        () =>
            roles.map((role) => ({
                id: role.id,
                key: role.id,
                label: role.name,
                icon: (
                    <TbShieldCheck size='75%' style={{ verticalAlign: '-0.125rem', color: token.colorTextSecondary }} />
                ),
                system: false,
                role,
            })),
        [roles, token.colorTextSecondary],
    );

    const filteredRoles = useMemo(() => {
        const normalizedValue = value.trim().toLowerCase();
        const entries = [...builtInRoles, ...customRoles];

        if (!normalizedValue) {
            return entries;
        }

        return entries.filter((role) => role.label.toLowerCase().includes(normalizedValue));
    }, [builtInRoles, customRoles, value]);

    return (
        <>
            <Typography.Title level={4}>
                Roles
                <PlanTag plan='pro' style={{ marginLeft: '0.75rem', verticalAlign: '0.25rem' }} />
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                View, create, and manage custom roles with fine-grained access to workspace resources.
            </Typography.Paragraph>
            <Divider style={{ margin: '1rem 0' }} />
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    <Input
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        prefix={<TbSearch style={{ marginRight: '0.325rem', color: token.colorTextQuaternary }} />}
                        placeholder='Search Roles...'
                        style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                    />
                </Col>
                <Col flex='none'>
                    <Button type='primary' onClick={() => setOpen(true)}>
                        <TbShieldPlus size='1rem' style={{ verticalAlign: '-0.125rem' }} />
                        Create Role
                    </Button>
                </Col>
            </Row>
            <CreateRoleModal open={open} setOpen={setOpen} />
            <Divider style={{ marginBottom: 0, marginTop: '1rem' }} />
            {loading ? (
                <SkeletonList listLength={6} minHeight='20rem' maxHeight='20rem' />
            ) : filteredRoles.length === 0 ? (
                <EmptySection
                    description={`No roles match "${value.trim()}". Clear the search to view workspace roles.`}
                    height='20rem'
                    icon={<TbUserSearch size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                    label='No Roles Found'
                    extra={
                        <Button size='small' onClick={() => setValue('')}>
                            Clear Search
                        </Button>
                    }
                />
            ) : (
                <List dataSource={filteredRoles} size='small' renderItem={(role) => <RoleListItem role={role} />} />
            )}
        </>
    );
};

export default Roles;
